import axios from 'axios';
import { getApiUrl } from '../config/runtimeConfig';

const API_BASE = getApiUrl();

// Authentication credentials are now held by HttpOnly cookies. The short-lived
// access token is intentionally not persisted in browser storage. This memory
// copy is only used when a caller explicitly needs a Bearer header; normal API
// traffic authenticates with cookies.
let memoryAccessToken = null;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

export function getAccessToken() { return memoryAccessToken; }

export async function bootstrapAccessToken() {
  try {
    const response = await api.get('/auth/bootstrap-access', { skipAuthRefresh: true, skipAuthRedirect: true });
    memoryAccessToken = response.data?.data?.accessToken || null;
    return memoryAccessToken;
  } catch (_) { memoryAccessToken = null; return null; }
}

export function setAuthTokens({ accessToken } = {}) {
  if (accessToken) memoryAccessToken = accessToken;
}

export function clearAuthStorage() {
  memoryAccessToken = null;
  try {
    localStorage.removeItem('9jawin_token');
    localStorage.removeItem('9jawin_refresh_token');
    localStorage.removeItem('9jawin_isAdmin');
  } catch (_) {}
  window.dispatchEvent(new Event('9jawin-auth-cleared'));
}

let refreshPromise = null;
export async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh-token', null, { skipAuthRefresh: true, skipAuthRedirect: true })
      .then((response) => {
        const tokens = response.data?.data || {};
        if (tokens.accessToken) memoryAccessToken = tokens.accessToken;
        return tokens.accessToken || null;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.request.use((config) => {
  // Cookies are the normal authentication mechanism. Keep a memory-only access
  // token fallback for a small number of legacy/integration callers.
  if (memoryAccessToken && !config.skipAuthHeader) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${memoryAccessToken}`;
  }
  return config;
});

function inspectAppVersionHeaders(response) {
  const latest = Number(response?.headers?.['x-app-latest-build']);
  const minimum = Number(response?.headers?.['x-app-min-build']);
  if (!Number.isSafeInteger(latest) && !Number.isSafeInteger(minimum)) return;
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('9jawin-app-version-hint', { detail: { latestBuildCode: latest, minimumBuildCode: minimum } }));
  } catch (_) {}
}

api.interceptors.response.use(
  (response) => { inspectAppVersionHeaders(response); return response; },
  async (error) => {
    const config = error.config || {};
    const authRequired = error.response?.headers?.['x-auth-required'] === 'true';
    const isAuthFailure = error.response?.status === 401 || authRequired;
    const isAuthEndpoint = /^\/auth\/(login|register|refresh-token|logout)/.test(config.url || '');
    if (!isAuthFailure || config.skipAuthRefresh || config._authRetried || isAuthEndpoint) return Promise.reject(error);
    try {
      const accessToken = await refreshAccessToken();
      if (!accessToken) throw new Error('Session refresh returned no access token.');
      config._authRetried = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      return api(config);
    } catch (refreshError) {
      clearAuthStorage();
      if (!config.skipAuthRedirect) {
        const authPages = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password'];
        if (!authPages.includes(window.location.pathname)) window.location.assign('/login');
      }
      return Promise.reject(refreshError);
    }
  }
);

api.settings = { getPublic: (config = {}) => api.get('/settings/public', config) };

api.avatars = {
  list: (config = {}) => api.get('/avatars', config),
};

api.user = {
  updateAvatar: (avatar_id, config = {}) => api.patch('/user/avatar', { avatar_id }, config),
};

api.profile = {
  getMe: (config = {}) => api.get('/profile/me', config),
  getMyLeaderboardRank: (config = {}) => api.get('/profile/me/leaderboard-rank', config),
  getMyPrime: (config = {}) => api.get('/profile/me/prime', config),
  getPublic: (userId, config = {}) => api.get(`/profile/${encodeURIComponent(userId)}`, config),
  update: (data, config = {}) => api.patch('/profile/me', data, config),
  requestEmailChange: (email, config = {}) => api.post('/profile/me/email/request-change', { email }, config),
  verifyEmailChange: (challengeId, code, config = {}) => api.post('/profile/me/email/verify-change', { challengeId, code }, config),
  updateAvatar: (fileOrUrl, config = {}) => {
    if (typeof fileOrUrl === 'string') return api.post('/profile/me/avatar', { avatarUrl: fileOrUrl }, config);
    const form = new FormData(); form.append('avatar', fileOrUrl); return api.post('/profile/me/avatar', form, { ...config, headers: { ...(config.headers || {}), 'Content-Type': 'multipart/form-data' } });
  },
  getBadges: (userId, config = {}) => api.get(`/profile/${encodeURIComponent(userId)}/badges`, config),
  setFeaturedBadges: (achievementKeys, config = {}) => api.put('/profile/me/featured-achievements', { achievementKeys }, config),
  getPrimeEmotes: (config = {}) => api.get('/profile/me/prime-emotes', config),
  setPrimeEmotes: (equipped, config = {}) => api.put('/profile/me/prime-emotes', { equipped }, config),
  getBadgeCatalog: (config = {}) => api.get('/profile/badges', config),
  getSettings: (config = {}) => api.get('/profile/settings', config),
  updateSettings: (data, config = {}) => api.patch('/profile/settings', data, config),
};

api.securityQuestions = {
  get: (config = {}) => api.get('/security-questions', config),
  save: (selections) => api.post('/security-questions', { selections }),
};

api.auth = {
  register: (data) => api.post('/auth/register', data, { skipAuthHeader: true, skipAuthRedirect: true }),
  verifyEmail: (data) => api.post('/auth/verify-email', data, { skipAuthHeader: true, skipAuthRedirect: true }),
  resendVerificationCode: (data) => api.post('/auth/verify-email/resend', data, { skipAuthHeader: true, skipAuthRedirect: true }),
  login: (data) => api.post('/auth/login', data, { skipAuthHeader: true, skipAuthRedirect: true }),
  google: (data, config = {}) => api.post('/auth/google', data, { skipAuthHeader: true, skipAuthRedirect: true, ...config }),
  completeGoogle: (data, config = {}) => api.post('/auth/google/complete', data, { skipAuthHeader: true, skipAuthRedirect: true, ...config }),
  googleStatus: (config = {}) => api.get('/auth/google/status', config),
  linkGoogle: (data, config = {}) => api.post('/auth/google/link', data, { skipAuthRedirect: true, ...config }),
  refreshToken: () => refreshAccessToken(),
  logout: () => api.post('/auth/logout', null, { skipAuthRefresh: true, skipAuthRedirect: true }),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: (config = {}) => api.get('/auth/profile', config),
  getPublicProfile: (publicId, config = {}) => api.get(`/auth/profile/id/${encodeURIComponent(publicId)}`, config),
  updateProfile: (data) => api.put('/auth/profile', data),
};

api.gameHistory = { list: (params = {}, config = {}) => api.get('/profile/me/game-history', { ...config, params }), getMatch: (recordType, recordId) => api.get('/profile/me/game-history/' + encodeURIComponent(recordType) + '/' + encodeURIComponent(recordId)) };
api.wallet = { getWallet: () => api.get('/wallet'), getTransactions: (params) => api.get('/wallet/transactions', { params }), getTransaction: (id) => api.get('/wallet/transactions/' + encodeURIComponent(id)), deposit: (data) => api.post('/wallet/deposit', data), verifyDeposit: (ref) => api.get(`/wallet/deposit/status/${encodeURIComponent(ref)}`), withdraw: (data) => api.post('/wallet/withdraw', data), resolveAccount: (data) => api.post('/wallet/resolve-account', data), savedAccounts: () => api.get('/wallet/saved-accounts'), saveAccount: (data) => api.post('/wallet/saved-accounts', data), verifySaveAccount: (data) => api.post('/wallet/saved-accounts/verify', data), deleteAccount: (id) => api.delete(`/wallet/saved-accounts/${encodeURIComponent(id)}`), verifyRemoveAccount: (id, data) => api.post(`/wallet/saved-accounts/${encodeURIComponent(id)}/verify-remove`, data), setDefaultAccount: (id) => api.patch(`/wallet/saved-accounts/${encodeURIComponent(id)}/default`) };
api.stakes = { getConfigs: (gameType) => api.get('/stakes/configs', { params: { gameType } }), placeStake: (data) => api.post('/stakes/place', data), getTiers: (playerCount) => api.get(`/stakes/${playerCount}`), joinTier: (tierId) => api.post(`/stakes/${tierId}/join`), getQueueStatus: (tierId) => api.get(`/stakes/${tierId}/status`), getActive: () => api.get('/stakes/active'), cancelQueue: () => api.post('/stakes/cancel'), cancelMatch: (tierId) => api.post(`/stakes/${tierId}/cancel`), cancelMatchById: (matchId) => api.post(`/stakes/match/${matchId}/cancel`) };
api.tournaments = { getAll: (params) => api.get('/tournaments', { params }), getById: (id) => api.get(`/tournaments/${id}`), getLeaderboard: (id) => api.get(`/tournaments/${id}/leaderboard`), getPayouts: (id) => api.get(`/tournaments/${id}/payouts`), join: (id) => api.post(`/tournaments/${id}/join`), startPlay: (id) => api.post(`/tournaments/${id}/play/start`), endPlay: (id, playId, data) => api.post(`/tournaments/${id}/play/${playId}/end`, data), forfeitPlay: (id, playId) => api.post(`/tournaments/${id}/play/${playId}/forfeit`), getMyTournaments: () => api.get('/tournaments/my/list'), getMyPayouts: () => api.get('/tournaments/my/payouts') };
api.leaderboards = { getGlobal: () => api.get('/leaderboards/global'), getByGame: (gameType) => api.get(`/leaderboards/${gameType}`), getPlayerStats: (userId) => api.get(`/leaderboards/player/${userId}`) };
api.friends = { search: (q) => api.get('/friends/search', { params: { q } }), getAll: () => api.get('/friends'), getRequests: () => api.get('/friends/requests'), sendRequest: (userId) => api.post(`/friends/add/${userId}`), acceptRequest: (friendshipId) => api.post(`/friends/${friendshipId}/accept`), declineRequest: (friendshipId) => api.post(`/friends/${friendshipId}/decline`), removeFriend: (friendshipId) => api.delete(`/friends/${friendshipId}`), blockUser: (userId) => api.post(`/friends/block/${userId}`) };
api.referrals = { getStats: () => api.get('/referrals'), getReferredUsers: () => api.get('/referrals/referred-users'), getEarnings: (params) => api.get('/referrals/earnings', { params }) };
api.customerSupport = {
  createConversation: (data) => api.post('/customer-support/conversations', data),
  createTicket: (id,data,files=[]) => { const list=Array.isArray(files)?files:[]; const url='/customer-support/conversations/'+encodeURIComponent(id)+'/ticket'; if(!list.length)return api.post(url,data); const form=new FormData(); Object.entries(data||{}).forEach(([k,v])=>form.append(k,v==null?'':String(v))); list.slice(0,3).forEach(f=>form.append('files',f)); return api.post(url,form,{headers:{'Content-Type':'multipart/form-data'}}); },
  listConversations: () => api.get('/customer-support/conversations'),
  getConversation: (id) => api.get(`/customer-support/conversations/${encodeURIComponent(id)}`),
  sendMessage: (id, content, files=[]) => { const list=Array.isArray(files)?files:[]; if(!list.length)return api.post(`/customer-support/conversations/${encodeURIComponent(id)}/messages`,{content}); const form=new FormData(); form.append('content',content); list.slice(0,3).forEach(f=>form.append('files',f)); return api.post(`/customer-support/conversations/${encodeURIComponent(id)}/messages`,form,{headers:{'Content-Type':'multipart/form-data'}}); },
  reopen: (id) => api.post(`/customer-support/conversations/${encodeURIComponent(id)}/reopen`),
  escalate: (id, reason) => api.post(`/customer-support/conversations/${encodeURIComponent(id)}/escalate`, { reason }),
  admin: {
    dashboard: () => api.get('/customer-support/admin/dashboard'),
    cases: (params) => api.get('/customer-support/admin/cases',{params}),
    getCase: (id) => api.get(`/customer-support/admin/cases/${encodeURIComponent(id)}`),
    reply: (id, content) => api.post(`/customer-support/admin/cases/${encodeURIComponent(id)}/reply`,{content}),
    note: (id, note) => api.post(`/customer-support/admin/cases/${encodeURIComponent(id)}/note`,{note}),
    update: (id, data) => api.patch(`/customer-support/admin/cases/${encodeURIComponent(id)}`,data),
    assign: (id, agentId) => api.post(`/customer-support/admin/cases/${encodeURIComponent(id)}/assign`,{agentId}),
    escalate: (id, reason) => api.post(`/customer-support/admin/cases/${encodeURIComponent(id)}/escalate`,{reason}),
    investigate: (id, toolName, args={}) => api.post(`/customer-support/admin/cases/${encodeURIComponent(id)}/investigate`,{toolName,args}),
    aiSettings: () => api.get('/customer-support/admin/ai-settings'),
    updateAiSettings: (data) => api.put('/customer-support/admin/ai-settings',data),
    testAi: (data={}) => api.post('/customer-support/admin/ai-settings/test',data),
    roles: () => api.get('/customer-support/admin/roles'),
    setRole: (userId, role) => api.put(`/customer-support/admin/roles/${userId}`,{role}),
    users: (params) => api.get('/customer-support/admin/queue/by-user',{params}),
    messageUser: (userId, content) => api.post('/customer-support/admin/users/' + encodeURIComponent(userId) + '/messages',{content}),
  },
  gmail: {
    status: () => api.get('/customer-support/email/admin/status'),
    authorize: () => api.get('/customer-support/email/admin/settings'),
    disconnect: () => api.delete('/customer-support/email/admin/password'),
    sync: () => api.post('/customer-support/email/admin/sync'),
    watch: () => api.post('/customer-support/email/admin/sync'),
    inbox: (params={}) => api.get('/customer-support/email/admin/inbox',{params}),
    send: (caseId,data) => api.post(`/customer-support/email/admin/cases/${encodeURIComponent(caseId)}/email`,data),
    uploadAttachment: (caseId,file) => { const form=new FormData(); form.append('file',file); return api.post(`/customer-support/email/admin/cases/${encodeURIComponent(caseId)}/email-attachment`,form,{headers:{'Content-Type':'multipart/form-data'}}); },
    assign: (messageId,caseId) => api.post(`/customer-support/email/admin/messages/${encodeURIComponent(messageId)}/assign`,{caseId}),
  },
  email: {
    settings: () => api.get('/customer-support/email/admin/settings'),
    save: (data) => api.put('/customer-support/email/admin/settings',data),
    testConnection: () => api.post('/customer-support/email/admin/test-connection'),
    clearPassword: () => api.delete('/customer-support/email/admin/password'),
  }};
api.notifications = { list: (params) => api.get('/notifications', { params }), markRead: (id) => api.patch(`/notifications/${id}/read`),
    getCTA: (id) => api.get(`/notifications/${id}/cta`), markAnnouncementRead: (id) => api.patch(`/notifications/announcements/${id}/read`), markAnnouncementDismiss: (id) => api.patch(`/announcements/${id}/dismiss`), trackAnnouncementEvent: (id,eventType,ctaIndex=null) => api.post(`/announcements/${id}/events`, { eventType, ...(ctaIndex===null?{}:{ctaIndex}) }), markAllRead: () => api.post('/notifications/read-all') };
api.achievements = { getAll: (config = {}) => api.get('/achievements', config), getUnlocked: () => api.get('/achievements/unlocked'), getSummary: () => api.get('/achievements/summary'), getById: (id) => api.get(`/achievements/${encodeURIComponent(id)}`) };
api.rooms = { create: (data) => api.post('/rooms', data), joinPublic: (roomId) => api.post(`/rooms/${roomId}/join`), joinPrivate: (code) => api.post('/rooms/join-private', { code }), getPublic: () => api.get('/rooms/public'), getById: (roomId) => api.get(`/rooms/${roomId}`), cancel: (roomId) => api.post(`/rooms/${roomId}/cancel`), start: (roomId) => api.post(`/rooms/${roomId}/start`), getMyInvitations: () => api.get('/rooms/invitations/mine'), getRoomInvitations: (roomId) => api.get(`/rooms/${roomId}/invitations`), inviteFriend: (roomId, invitedUserId) => api.post(`/rooms/${roomId}/invitations`, { invitedUserId }), acceptInvitation: (invitationId) => api.post(`/rooms/invitations/${invitationId}/accept`), declineInvitation: (invitationId) => api.post(`/rooms/invitations/${invitationId}/decline`), cancelInvitation: (invitationId) => api.post(`/rooms/invitations/${invitationId}/cancel`) };

export default api;
